import React from 'react'
import { getRuntimeFeatureFlag, useRuntimeConfig } from '../../config/runtimeConfig'
import './CollationStudioLandingPage.css'

const witnessHeaders = [
  'IM-P3465',
  'IM-P3473',
  'IM-BWII672',
  'IM-P3471',
  'IM-P5881',
  'IM-I344',
  'IM-P400',
  'IM-L4044'
]

const ledgerRows = [
  {
    label: 'IM 001',
    cells: [
      'باب عرض الكتاب ترجمة عبد الله بن المقفّع',
      'الباب الأوّل باب عبد الله ابن المقفّع لهذا الكتاب ومدحه وما يتعلّق به',
      null,
      'باب عبد الله بن *المقفّع الذي ترجم هذا الكتاب من الفارسيّة إلى العربيّة',
      null,
      'بسم الله الرحمن الرحيم وبه ثقتي هذا كتاب فيه كليلة ودمنة',
      'الباب الأوّل في ذكر ابن المقفّع الذي ترجم به هذا الكتاب',
      'كتاب كليلة ودمنة'
    ]
  },
  {
    label: 'IM 006',
    cells: [
      null,
      null,
      null,
      'ويُلزمون أنفسهم العناية بذلك ويناقض بعضهم البعض',
      null,
      'ويُلزمون أنفسهم العناية بذلك والاجتماع له ومناقضة بعضهم بعضًا فيه',
      'ويأمروا أنفسهم العناية بذلك وتناقص بعضهم بعضًا',
      null
    ]
  },
  {
    label: 'IM 007',
    cells: [
      null,
      null,
      null,
      null,
      null,
      'وأدقّوها لهم من ذلك فوضعوا لكلّ ما أدركوا من ذلك',
      'فصنعوا ما أرادوا من ذلك *وبلغوه بطول أعمارهم',
      null
    ]
  },
  {
    label: 'IM 010',
    cells: [
      null,
      'وإنّ مثله في ذلك ومثل واضعيه كمثل الزارع إنّما يصلح أرضه ويعمّرها',
      'وإنّ مثله كالزارع الذي حرث أرضه وعمّرها لابتغاء الزرع',
      null,
      null,
      null,
      null,
      null
    ]
  },
  {
    label: 'IM 014',
    cells: [
      null,
      null,
      null,
      null,
      'لما لم يكن وإذا كثرت صنوف العلم وأصوله ثمّ كثرت فروع كلّ صنف منها',
      null,
      'ولم يكن إذا كثرت صنوف العلم ثمّ كثرت فروع كلّ صنف منها',
      'ولم يكن لمّا أن كثرت فروع كل صنف منها حتى لا يُستكمل منها شيء'
    ]
  },
  {
    label: 'IM 017',
    focus: 6,
    cells: [
      null,
      null,
      'وإنّه إن قرأها ولم يتمعّنها أخذها على أيّ وجه أراده',
      null,
      'قال ابن المقفّع فأوّل ما ينبغي لمن قرأ هذا الكتاب ونظر فيه',
      'وإنّما ينبغي لمن قرأ هذا الكتاب وأحبّ الاقتداء بمن كان قبله',
      'فأوّل ما ينبغي لمن يقرأ هذا الكتاب واجب الاقتداء بمن كان قبله',
      'فأوّل ما ينبغي لمن طلب هذا الكتاب أن يبتدئ بجودة قراءته'
    ]
  }
]

const variorumSubtitle = 'Fluid Textual Traditions Collation Environment'

const featureItems = [
  {
    title: 'Draft collation proposals',
    text: 'Prepare AI-assisted segmentations and alignments as synoptic editions for human review.'
  },
  {
    title: 'Multi-witness collation',
    text: 'Compare many witnesses in one structured editorial table.'
  },
  {
    title: 'Cross-lingual collation',
    text: 'Align witnesses and translations across language traditions.'
  },
  {
    title: 'Editorial controls',
    text: 'Split, merge, shift boundaries, mark gaps, and preserve provenance.'
  }
]

const extractShareId = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const urlMatch = trimmed.match(/\/shared-ledgers\/([^/?#]+)/)
  if (urlMatch?.[1]) {
    return urlMatch[1]
  }
  if (/^[A-Za-z0-9_-]{8,}$/.test(trimmed)) {
    return trimmed
  }
  return null
}

const CollationStudioLandingPage: React.FC = () => {
  const { config } = useRuntimeConfig()
  const requestAccessEnabled = getRuntimeFeatureFlag(config, 'landing.requestAccess')
  const ledgerSafeGuideEnabled = getRuntimeFeatureFlag(config, 'documentation.ledgerSafeGuide')

  const handleOpenSharedLedger = () => {
    const value = window.prompt('Paste a Variorum shared collation link or share ID.')
    const shareId = value ? extractShareId(value) : null
    if (!shareId) {
      return
    }
    window.location.href = `/shared-ledgers/${encodeURIComponent(shareId)}`
  }

  return (
    <main className="cs-landing-page">
      <section className="cs-landing-hero">
        <div className="cs-landing-ledger-field" aria-hidden="true">
          <div className="cs-landing-head">Row</div>
          {witnessHeaders.map(header => (
            <div className="cs-landing-head" key={header}>{header}</div>
          ))}
          {ledgerRows.flatMap(row => [
            <div className="cs-landing-row-head" key={`${row.label}-head`}>{row.label}</div>,
            ...row.cells.map((cell, index) => (
              <div
                className={[
                  'cs-landing-arabic',
                  cell ? '' : 'cs-landing-gap-cell',
                  row.focus === index ? 'cs-landing-focus-cell' : ''
                ].filter(Boolean).join(' ')}
                key={`${row.label}-${index}`}
              >
                {cell || <span>Gap</span>}
                {row.focus === index ? (
                  <div className="cs-landing-edit-ribbon" dir="ltr">
                    <span>Single</span>
                    <b>Move ↑</b>
                    <b>Move ↓</b>
                    <b>Ins ↑</b>
                    <b>Ins ↓</b>
                    <b>Split</b>
                  </div>
                ) : null}
              </div>
            ))
          ])}
        </div>
        <div className="cs-landing-scrim" />

        <header className="cs-landing-topbar">
          <div className="cs-landing-brand">
            <div className="cs-landing-brand-title">VARIORUM</div>
            <div className="cs-landing-brand-sub">{variorumSubtitle}</div>
          </div>
          <div className="cs-landing-institutional-lockup" aria-label="Institutional partners">
            <a href="https://www.fu-berlin.de/" target="_blank" rel="noreferrer">
              <img src="/brand/fu-berlin-logo.jpg" alt="Freie Universität Berlin" />
            </a>
            <span className="cs-landing-lockup-rule" aria-hidden="true" />
            <a href="https://www.kalila-and-dimna.fu-berlin.de/" target="_blank" rel="noreferrer">
              <img src="/brand/kd-edition-logo.png" alt="Kalīla wa-Dimna Edition" />
            </a>
          </div>
          {requestAccessEnabled || ledgerSafeGuideEnabled ? (
            <nav className="cs-landing-actions" aria-label="Support links">
              {ledgerSafeGuideEnabled ? <a href="/guide/collation-environment">Guide</a> : null}
              {requestAccessEnabled ? (
                <a href="mailto:access@collationstudio.com?subject=VARIORUM%20access">Request access</a>
              ) : null}
            </nav>
          ) : null}
        </header>

        <div className="cs-landing-content">
          <div className="cs-landing-eyebrow">{config.releaseName || 'August 2026 Ledger Safe Beta 1'}</div>
          <h1>VARIORUM</h1>
          <p className="cs-landing-main-subtitle">{variorumSubtitle}</p>
          <div className="cs-landing-access-panel">
            <a className="cs-landing-primary" href="/workbench">Sign in with FU Email</a>
            <button className="cs-landing-secondary" type="button" onClick={handleOpenSharedLedger}>
              Open Shared Collation
            </button>
          </div>
        </div>

        <div className="cs-landing-preview-strip">
          {featureItems.map(item => (
            <div className="cs-landing-preview-item" key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  )
}

export default CollationStudioLandingPage
